import { FormEvent, useState, useEffect } from "react";

import { Button } from "@/components/Button/Button";
import { TextInput } from "@/components/widget-components";
import { ImageData } from "@/requests/signup";
import { Select, SelectItem } from "./DocumentTypeSelect";
import { FileUploader } from "./FileUploader";

const legalIDOptions = [
  "Canadian Passport",
  "Non-Canadian Passport",
  "Provincial Driver 's License",
  "Provincial ID Card",
  "Permanent Resident Card",
];

type UploadIDProps = {
  onConfirm: (
    fileData: ImageData,
    accountType: string,
    idNumber: string
  ) => void;
};

// Helper function to check if file is an image
const isImageFile = (fileData: ImageData): boolean => {
  return (
    fileData.type.startsWith("image/") || (fileData as any).isImage === true
  );
};

// Helper function to get file extension
const getFileExtension = (fileName: string): string => {
  return fileName.substring(fileName.lastIndexOf(".") + 1).toUpperCase();
};

export const UploadID = ({ onConfirm }: UploadIDProps) => {
  const [idType, setIdType] = useState("");
  const [idNumber, setIdNumber] = useState("");
  const [error, setError] = useState("");
  const [fileData, setFileData] = useState<ImageData | null>(null);

  const handleSuccess = (fileData: ImageData) => {
    setFileData(fileData);
    setError("");
  };

  const handleConfirm = (e: FormEvent) => {
    e.preventDefault();
    if (fileData) {
      onConfirm(fileData, idType, idNumber);
    }
  };

  // Check if form is valid and ready for submission
  const isFormValid = fileData && !error && idType && idNumber;

  // Add Enter key handler
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Only proceed if Enter key is pressed
      if (e.key === "Enter") {
        e.preventDefault();

        if (isFormValid) {
          // Create a synthetic event for the form submission
          const syntheticEvent = {
            preventDefault: () => {},
          } as FormEvent;
          handleConfirm(syntheticEvent);
        } else {
          // Show error message when form is invalid
          setError("id number is invalid");
        }
      }
    };

    // Add event listener
    document.addEventListener("keydown", handleKeyPress);

    // Remove event listener on cleanup
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [isFormValid, fileData, idType, idNumber]); // Dependencies that affect form validation

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={handleConfirm}
      data-testid="form-upload-id"
    >
      <div data-testid="text-upload-id-instruction">
        Upload a photo of your passport or driver&apos;s license. Make sure all
        details are legible and that corners of the document are visible in the
        photo.
      </div>
      <div className="md:px-10" data-testid="container-upload-id-type">
        <Select
          onValueChange={(val) => setIdType(val.toLowerCase())}
          data-testid="select-upload-id-type"
        >
          {legalIDOptions.map((docType) => (
            <SelectItem
              key={docType}
              value={docType}
              data-testid={`item-upload-id-${docType.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {docType}
            </SelectItem>
          ))}
        </Select>
      </div>
      <div data-testid="container-upload-id-number">
        <TextInput
          autoFocus
          placeholder="ID Number"
          onChange={(e) => setIdNumber(e.target.value)}
          data-testid="input-upload-id-number"
        />
      </div>
      {error && (
        <div className="text-rose-600" data-testid="error-upload-id-message">
          {error}
        </div>
      )}
      {fileData && (
        <div
          className="flex justify-center invisible md:visible"
          data-testid="container-upload-id-preview"
        >
          {isImageFile(fileData) ? (
            <img
              src={fileData.url}
              width={100}
              alt="Document preview"
              data-testid="img-upload-id-preview"
            />
          ) : (
            <div className="flex flex-col items-center p-4 border border-gray-300 rounded">
              <div className="text-4xl mb-2">📄</div>
              <div className="text-sm text-gray-600">
                {getFileExtension(fileData.name)} File
              </div>
              <div className="text-xs text-gray-500 truncate max-w-[100px]">
                {fileData.name}
              </div>
            </div>
          )}
        </div>
      )}

      <FileUploader
        onUploadComplete={handleSuccess}
        onError={setError}
        customButtonText=""
        showUploadIcon={true}
        data-testid="container-upload-id"
      />
      <Button
        type="submit"
        disabled={!isFormValid}
        data-testid="btn-upload-id-confirm"
      >
        Confirm
      </Button>
    </form>
  );
};
